import { Builder, CustomSerializer, Field, From, inc, Schema, DateSerializer as TimeSerializer, use } from '@bonusly/schema';

import { RuleGroup, RuleGroupBuilder } from './groups';
import { DateOnlySerializer } from './serializers';
import { User, UserBuilder } from './users';

// Customer serializers

// Custom serizlier to convert null values to Infinity and vice versa. This allows checks to be made against the value
// without needing to check for null values as the value will always be a type of number.
class NominationLimitSerializer extends CustomSerializer {
	deserialize(data: number | null): number {
		return data ?? Infinity;
	}

	serialize(data: number): number | null {
		return data === Infinity ? null : data;
	}
}

// Schema

export type NominationPeriodStatus = 'open' | 'upcoming' | 'closed';

@Schema()
export class Nomination {
	@Field()
	@From('id')
	public id = '';

	@Field()
	@From('reason')
	public reason = '';

	@Field()
	@From('nominationPeriodId')
	public nominationPeriodId = '';

	@Field()
	@From('nominatorId')
	public nominatorId = '';

	@Field()
	@From('nominatedUserId')
	public nominatedUserId = '';

	@Field(User)
	@From('nominator')
	public nominator = new User();

	@Field(User)
	@From('nominatedUser')
	public nominatedUser = new User();

	@Field(TimeSerializer)
	@From('createdAt')
	public createdAt = new Date();
}

@Schema()
export class NominatedUserSummary {
	@Field()
	@From('userId')
	public userId = '';

	@Field()
	@From('summary')
	public summary = '';

	@Field(User)
	@From('user')
	public user = new User();
}

@Schema()
export class AdminNominationPeriod {
	@Field()
	@From('id')
	public id = '';

	@Field()
	@From('name')
	public name = '';

	@Field()
	@From('description')
	public description = '';

	@Field(DateOnlySerializer)
	@From('startDate')
	public startDate = new Date();

	@Field(DateOnlySerializer)
	@From('endDate')
	public endDate: Date | null = null;

	@Field()
	@From('uniqueNominatedUserCount')
	public nominees = 0;

	@Field()
	@From('totalNominations')
	public votes = 0;

	@Field()
	@From('status')
	public status: NominationPeriodStatus = 'open';

	@Field()
	@From('creator')
	public creator: User = new User();

	get detailPath() {
		return `/admin/programs/nominations/${this.id}`;
	}

	get editPath() {
		return `/admin/programs/nominations/${this.id}/edit`;
	}

	get isUpcoming() {
		return this.status === 'upcoming';
	}

	get isClosed() {
		return this.status === 'closed';
	}
}

@Schema()
export class AdminNominationPeriodDetail {
	@Field()
	@From('id')
	public id = '';

	@Field()
	@From('name')
	public name = '';

	@Field()
	@From('description')
	public description = '';

	@Field(DateOnlySerializer)
	@From('startDate')
	public startDate = new Date();

	@Field(DateOnlySerializer)
	@From('endDate')
	public endDate: Date | null = null;

	@Field()
	@From('status')
	public status: NominationPeriodStatus = 'open';

	@Field()
	@From('nominationLimit')
	public nominationLimit: number | null = null;

	@Field([Nomination])
	@From('nominations')
	public nominations: Nomination[] = [];

	@Field(RuleGroup)
	@From('nomineeRulesGroup')
	public nomineeRulesGroup: RuleGroup | null = null;

	@Field(RuleGroup)
	@From('nominatorRulesGroup')
	public nominatorRulesGroup: RuleGroup | null = null;

	@Field([String])
	@From('favoritedUserIds')
	public favoritedUserIds: string[] = [];

	@Field([NominatedUserSummary])
	@From('nominatedUserSummaries')
	public nominatedUserSummaries: NominatedUserSummary[] = [];

	@Field()
	@From('visibility')
	public visibility: 'admin' | 'public' = 'admin';

	public get isUpcoming() {
		return this.status === 'upcoming';
	}

	public get isOpen() {
		return this.status === 'open';
	}

	public get isClosed() {
		return this.status === 'closed';
	}

	public get isPublic() {
		return this.visibility === 'public';
	}
}

@Schema()
export class UserNominationPeriod {
	@Field()
	@From('id')
	public id = '';

	@Field()
	@From('name')
	public name = '';

	@Field()
	@From('description')
	public description = '';

	@Field(NominationLimitSerializer)
	@From('nominationLimit')
	public nominationLimit = Infinity;

	@Field(DateOnlySerializer)
	@From('startDate')
	public startDate = new Date();

	@Field(DateOnlySerializer)
	@From('endDate')
	public endDate: Date | null = null;

	@Field()
	@From('status')
	public status: NominationPeriodStatus = 'open';

	@Field([Nomination])
	@From('currentUserNominations')
	public currentUserNominations: Nomination[] = [];

	@Field(RuleGroup)
	@From('nomineeRulesGroup')
	public nomineeRulesGroup: RuleGroup | null = null;

	get canSubmitNomination() {
		return this.nominationLimit === null || this.currentUserNominations.length < this.nominationLimit;
	}
}

// Builders

export const NominationBuilder = new Builder(Nomination, {
	id: inc(String),
	reason: 'Test Reason',
	nominationPeriodId: '1',
	nominatorId: '1',
	nominatedUserId: '2',
	nominatedUser: use(UserBuilder),
	nominator: use(UserBuilder),
});

export const UserNominationPeriodBuilder = new Builder(UserNominationPeriod, {
	id: inc(String),
	name: 'Test Nomination Period',
	description: 'Test Description',
	startDate: () => new Date(),
	endDate: () => new Date(),
	status: 'open',
	currentUserNominations: use([NominationBuilder]),
	nomineeRulesGroup: use(RuleGroupBuilder),
});

export const AdminNominationPeriodBuilder = new Builder(AdminNominationPeriod, {
	id: inc(String),
	name: 'Test Nomination Period',
	description: 'Test Description',
	startDate: () => new Date(),
	endDate: null,
	nominees: 10,
	votes: 10,
	status: 'open',
	creator: use(UserBuilder),
});

export const AdminNominationPeriodDetailBuilder = new Builder(AdminNominationPeriodDetail, {
	id: inc(String),
	name: 'Test Nomination Period',
	description: 'Test Description',
	startDate: () => new Date(),
	endDate: null,
	status: 'open',
	nominationLimit: null,
	nomineeRulesGroup: use(RuleGroupBuilder),
	nominatorRulesGroup: use(RuleGroupBuilder),
	nominations: use([NominationBuilder], 2),
	favoritedUserIds: () => [],
	visibility: 'admin',
});
